import { useAuth } from "@clerk/nextjs";
import { useMutation, useQuery } from "@tanstack/react-query";
import { apiBaseUrl, fetchWrapper } from "~/utils/utils";
import { BaseProps } from "./base";
interface UploadFilePayload {
    formData: FormData;
}

interface GeneratePresignedUrlPayload {
    file_name: string;
    file_type: string;
}

export const useUploadFile = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const { getToken } = useAuth();

    const mutationFn = async (payload: UploadFilePayload) => {
        const token = await getToken();
        const result = await fetch(`${apiBaseUrl}/files/upload`, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${token}`
            },
            body: payload.formData
        });
        const data = await result.json();

        if (!result.ok) {
            const error = new Error(data.message || 'An error when uploading file occurred');
            throw error;
        }

        return data
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess: async (data, variables) => {
            onSuccess?.(data, variables);
        },
        onError: (error) => {
            onError?.(error);
        }
    })

    return mutation;
}

export const useGetCompanyLogo = (orgId: string, fileName: string) => {
    const queryFn = async () => {
        const storedData = localStorage.getItem('company-logo');
        const currentTime = new Date().getTime();
        if (storedData) {
            const { expiration_date, presigned_url } = JSON.parse(storedData);
            if (currentTime < new Date(expiration_date).getTime()) {
                return presigned_url
            }
        }

        const url = `${apiBaseUrl}/public-files/company-logo?org_id=${orgId}&file_name=${fileName}`;
        const result = await fetchWrapper<{ presigned_url: string; expiration_date: number }>({
            url,
            method: 'GET',
            showErrorToast: false,
            showSuccessToast: false,
        });

        localStorage.setItem('company-logo', JSON.stringify(result));
        return result?.presigned_url;
    }

    const query = useQuery({
        queryKey: ['getPresignedUrl', orgId, fileName],
        queryFn,
        enabled: !!orgId && !!fileName,
    });

    return query;
}

export const useGeneratePresignedUrl = () => {
    const { getToken } = useAuth();

    const mutationFn = async (payload: GeneratePresignedUrlPayload) => {
        const token = await getToken();
        const result = await fetch(`${apiBaseUrl}/files/presigned-url?fileType=${payload.file_type}&fileName=${payload.file_name}`, {
            method: 'GET',
            headers: {
                'Content-Type': 'application.json',
                'Authorization': `Bearer ${token}`
            },
        });
        const data = await result.json();

        if (!result.ok) {
            const error = new Error(data.message || 'An error when generating presigned url occurred');
            throw error;
        }

        return data
    }

    const mutation = useMutation({
        mutationFn,
    })

    return mutation;
}

export const useUploadFileAndGetPresignedUrl = (props: BaseProps) => {
    const { onSuccess, onError } = props;
    const uploadFileMutation = useUploadFile(props);
    const getPresignedUrlMutation = useGeneratePresignedUrl();

    const mutationFn = async (payload: UploadFilePayload): Promise<string> => {
        const uploadResponse = await uploadFileMutation.mutateAsync(payload);
        const filename = uploadResponse.file_name.split('/').pop();

        const presignedUrlResponse = await getPresignedUrlMutation.mutateAsync({
            file_name: filename,
            file_type: payload.formData.get('file_type') as string
        });
        return presignedUrlResponse.presigned_url;
    }

    const mutation = useMutation({
        mutationFn,
        onSuccess,
        onError,
    })

    return mutation
}

